import React, { useRef, useState } from 'react'
import { useSize } from 'components/Charts_Assets'
import { scaleLinear } from 'd3-scale'
import { line } from 'd3-shape'
import './styles.scss'
import Tooltip from 'components/Charts_Tooltip'
import XTicks from 'components/Charts_XTicks'
import YTicks from 'components/Charts_YTicks'
import Value from 'components/Charts_Value'

type AnchorType = 'start' | 'end' | 'middle'
type ChartProps = {
  data: any[]
  yDomain: [number, number]
  xDomain: [Date, Date]
  className: string
  yTicks?: { text?: number[]; lines?: number[] }
  xTicks?: { text?: string[]; lines?: string[] }
  height: number
  unit?: string
  annotations?: { d: string; v: number; p: string; t: string }[][]
}
export const MultiLine: React.FC<ChartProps> = ({
  data,
  height = 40,
  yDomain,
  xDomain,
  className,
  yTicks,
  xTicks,
  unit = '%',
  annotations,
}) => {
  const [container, size] = useSize()

  const [ttData, setTtData] = useState<
    | {
        pos: { x: number; anchor: AnchorType }
        data?: { d: string; v: number; p: string }[]
      }
    | undefined
  >()

  const createTooltip = (e) => {
    const xgraph = xAxisScale.invert(Math.floor(e.nativeEvent.layerX))

    const date = new Date(xgraph).toISOString().split('T')[0]
    const year = +date.split('-')[0]

    const els = data.map((m) => {
      return m.find((f, i) => {
        const before = +f.d.split('-')[0]
        const after = +(m[i + 1] || { d: '2200' }).d.split('-')[0]
        return (
          (i === 0 ? true : before <= year) &&
          (i === m.length - 1 ? true : year <= after)
        )
      })
    })

    if (els.length === 0) return null

    const x = xAxisScale(new Date(els[0].d))
    const ttdata = {
      pos: {
        x: x,
        anchor: (x > size.width / 2 ? 'end' : 'start') as AnchorType,
      },
      data: els,
    }
    setTtData(ttdata)
  }
  const cancelTooltip = () => setTtData(undefined)

  const svg = useRef<SVGSVGElement>(null)
  //

  const yFunction = (d: any) =>
    yAxisScale.range()[0] + yAxisScale.range()[1] - yAxisScale(d.v)

  const xFunction = (d: any, i: number) => {
    return xAxisScale(new Date(d.d))
  }

  const width = size.width
  const gutterTop = 0
  const gutterRight = size.isMobile ? 0 : 0
  const gutterLeft = 0
  const gutterBottom = 0
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom,
  }

  const xAxisScale = React.useMemo(
    () => scaleLinear().domain(xDomain).range([plotArea.left, plotArea.right]),
    [size, xDomain]
  )

  const yAxisScale = React.useMemo(
    () => scaleLinear().domain(yDomain).range([plotArea.bottom, plotArea.top]),
    [size, xDomain]
  )
  const lineFunction = React.useMemo(
    () => line().x(xFunction).y(yFunction),
    [size, xDomain]
  )

  // console.log(data)

  return (
    <div className={'multiline ' + className}>
      <div
        ref={container}
        style={{ position: 'relative', width: '100%', height: height }}
      >
        <svg
          // style={{ colorScheme: 'dark' }}
          ref={svg}
          height={height}
          onTouchMove={createTooltip}
          onMouseMove={createTooltip}
          onMouseLeave={cancelTooltip}
          onTouchCancel={cancelTooltip}
        >
          {xTicks && !ttData && (
            <XTicks data={xTicks} x={xAxisScale} y={yAxisScale} />
          )}
          {yTicks && <YTicks data={yTicks} x={xAxisScale} y={yAxisScale} />}
          {data.length > 0 &&
            data.map((d, i) => {
              // console.log(lineFunction(d))
              return (
                <React.Fragment key={'Multiline_' + i}>
                  <path d={lineFunction(d)} className={'_' + i} stroke='#fff' />
                  {!ttData && (
                    <Value
                      value={d[d.length - 1].v}
                      x={xAxisScale(new Date(d[d.length - 1].d))}
                      y={yAxisScale}
                      offset={0}
                      textAnchor='start'
                      unit={unit}
                    />
                  )}
                </React.Fragment>
              )
            })}
          {annotations &&
            !ttData &&
            annotations.map((set, i) => {
              return (
                <g className={'annotation _' + i} key={'AnnotationSet_' + i}>
                  {set.map((anno, i) => {
                    return (
                      <g
                        transform={`translate(${xAxisScale(new Date(anno.d))},${
                          yAxisScale.range()[0] - yAxisScale(anno.v)
                        })`}
                        key={'Annotation_' + i}
                      >
                        <circle r={3} />
                        <text textAnchor={anno.p} y={-9}>
                          {anno.t}
                        </text>
                      </g>
                    )
                  })}
                </g>
              )
            })}
          {ttData && <Tooltip data={ttData} y={yAxisScale} />}
        </svg>
      </div>
    </div>
  )
}
